/* ============================================================
   HUIT ARCHIV scroll-scrubbed hero
   Companion to huit-motion.js. Same discipline:
   - transforms + opacity only, never layout
   - full prefers-reduced-motion / no-JS / failed-load static fallback
     (overlay always visible; the wrapper is one readable viewport)
   - self-hosted GSAP 3.13 + ScrollTrigger via data-attrs (Liquid-free)
   - survives the Shopify theme editor: rebuilds on section:load, reverts
     the gsap.context + kills its ScrollTriggers on section:unload, never
     leaks triggers, never leaves the section blank
   - no layout thrashing in the scrub: read video state, write currentTime;
     all overlay / flip values are GSAP-driven (one proxy, not per-frame DOM
     queries)

   The mechanic:
     Beat 1   scroll progress -> video.currentTime (folder opening). The
              video's last frame IS shot 1, so it dissolves into the flipbook.
     Beats 2-4 shot1 -> shot2 -> shot3 as a CSS 3D page turn around the spine.

   This file does nothing unless a [data-huit-hero][data-hero-scroll-eligible]
   section is present AND motion is allowed.
   ============================================================ */

(function () {
  'use strict';

  var self = document.currentScript;
  var GSAP_URL = (self && self.getAttribute('data-gsap-url')) || 'gsap.min.js';
  var ST_URL =
    (self && self.getAttribute('data-scrolltrigger-url')) || 'ScrollTrigger.min.js';

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Desktop-only scrubbing for now (mobile is a later pass). Below this width we
  // leave the static fallback in place.
  var DESKTOP_QUERY = '(min-width: 750px)';

  function eligibleSections() {
    return Array.prototype.slice.call(
      document.querySelectorAll('[data-huit-hero][data-hero-scroll-eligible]')
    );
  }

  // Nothing to do if there is no eligible hero, or motion is off. The Liquid /
  // CSS fallback is already showing the static frame + overlay.
  if (reduceMotion || !eligibleSections().length) return;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  var gsap, ScrollTrigger;
  // One gsap.context + teardown bundle per booted section, so a theme-editor
  // re-render can revert exactly what was built.
  var instances = [];

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  // Map a sub-window of overall progress [a..b] to local [0..1].
  function sub(p, a, b) {
    if (p <= a) return 0;
    if (p >= b) return 1;
    return (p - a) / (b - a);
  }

  function tearDownAll() {
    instances.forEach(function (inst) {
      try {
        if (inst.cancelVideoSync) inst.cancelVideoSync();
      } catch (e) {}
      try {
        if (inst.ctx) inst.ctx.revert();
      } catch (e) {}
      if (inst.node && inst.node.removeAttribute) {
        inst.node.removeAttribute('data-hero-scroll-on');
      }
    });
    instances = [];
  }

  function bootSection(section) {
    var track = section.querySelector('[data-hero-track]');
    var stage = section.querySelector('[data-hero-stage]');
    var video = section.querySelector('[data-hero-video]');
    var videoLayer = section.querySelector('[data-hero-video-layer]');
    var page1 = section.querySelector('[data-hero-page="1"]');
    var page2 = section.querySelector('[data-hero-page="2"]');
    // The turning leaf containers (front photo + back paper). JS rotates these.
    var leaf1 = page1 && page1.querySelector('[data-hero-leaf]');
    var leaf2 = page2 && page2.querySelector('[data-hero-leaf]');
    // Sweeping light on each leaf, and the soft shadow each leaf casts on the
    // page it reveals beneath.
    var shade1 = page1 && page1.querySelector('[data-hero-shade]');
    var shade2 = page2 && page2.querySelector('[data-hero-shade]');
    var cast1 = page1 && page1.querySelector('[data-hero-cast]');
    var cast2 = page2 && page2.querySelector('[data-hero-cast]');
    var mark = section.querySelector('[data-hero-mark] [data-huit-8-glyph]');
    var leak = section.querySelector('[data-hero-leak]');
    var counterEl = section.querySelector('[data-hero-counter]');
    var ruleEl = section.querySelector('[data-hero-rule]');
    var hint = section.querySelector('[data-hero-hint]');

    if (!track || !stage || !video) return;

    var inst = { node: section, ctx: null, cancelVideoSync: null };

    // ---- Video scrub plumbing -------------------------------------------------
    // We never set currentTime blindly every frame; we only write when the
    // target differs from the last write, and duration is read lazily (unknown
    // until metadata loads).
    var targetTime = 0;
    var lastWritten = -1;
    var duration = 0;
    var rafHandle = null;
    var usingRVFC = typeof video.requestVideoFrameCallback === 'function';

    // Seek the video to the current scroll-driven target, then go QUIET. This is
    // scheduled once per onUpdate (only when the target actually moved), never as
    // a free-running loop: scrubFlush re-arms nothing, so a static scroll position
    // costs zero work and the decoder is not thrashed at idle.
    //
    // The write is scheduled on rAF (always fires next frame, so the pending
    // guard always clears). When the platform has requestVideoFrameCallback we
    // additionally ask for one decoded-frame callback purely to settle the
    // displayed frame after the seek; it is fire-and-forget and never gates the
    // next requestSeek, so it cannot wedge the loop if no new frame is produced.
    function scrubFlush() {
      rafHandle = null;
      if (!duration) return;
      var t = clamp01(targetTime) * duration;
      // Only seek on a meaningful delta to spare the decoder.
      if (Math.abs(t - lastWritten) > 0.012) {
        try {
          video.currentTime = t;
          lastWritten = t;
          if (usingRVFC) {
            // Fire-and-forget: nudge the decoder to present the seeked frame.
            video.requestVideoFrameCallback(function () {});
          }
        } catch (e) {}
      }
    }

    // Request a SINGLE flush. If one is already pending, do nothing (coalesce).
    function requestSeek() {
      if (rafHandle != null) return;
      rafHandle = window.requestAnimationFrame(scrubFlush);
    }

    inst.cancelVideoSync = function () {
      if (rafHandle != null) window.cancelAnimationFrame(rafHandle);
      rafHandle = null;
    };

    function onMeta() {
      duration = video.duration || 0;
      // Park on the first frame so the seam into the still flipbook is correct
      // before any scroll happens.
      requestSeek();
    }
    if (video.readyState >= 1 && video.duration) {
      onMeta();
    } else {
      video.addEventListener('loadedmetadata', onMeta, { once: true });
      // Kick the decoder so metadata + seeking are responsive on first scroll.
      // Only when nothing has loaded yet, and never on a rebuild that already
      // has data: video.load() aborts the in-flight fetch and re-downloads.
      if (video.readyState < 1) {
        try {
          video.load();
        } catch (e) {}
      }
    }

    // ---- The scrubbed timeline -----------------------------------------------
    // GSAP-driven off ScrollTrigger progress. onUpdate fans the raw progress out
    // to the video target + the handful of elements that map from raw progress
    // (counter text, video-layer fade, light leak). The flips and the 8 mark are
    // their own tweens placed on the timeline at progress-fraction positions.
    inst.ctx = gsap.context(function () {
      // The sequence is paced with calm HOLD gaps between beats so it leafs
      // deliberately, not in a snap. Windows (of total scroll progress 0..1):
      //   0.00 - 0.30  folder opening (video scrub)
      //   0.30 - 0.42  hold on shot 1
      //   0.42 - 0.62  FLIP 1: shot 1 leaf turns to reveal shot 2 (20% window)
      //   0.62 - 0.74  hold on shot 2
      //   0.74 - 0.94  FLIP 2: shot 2 leaf turns to reveal shot 3 (20% window)
      //   0.94 - 1.00  hold on shot 3
      // Each flip gets a generous slice and an ease, so 0->90deg no longer
      // happens in a few px of scroll. The track itself is lengthened in the
      // Liquid (base 560vh) so each beat has room to breathe.
      var OPEN_END = 0.30;
      var FLIP1_START = 0.42;
      var FLIP1_END = 0.62;
      var FLIP2_START = 0.74;
      var FLIP2_END = 0.94;
      // Midpoints (leaf edge-on, next shot being revealed). The counter flips
      // here so the number lands WITH the new image; 004 (at FLIP_2_MID ~= 0.84)
      // holds stable to p=1.
      var FLIP_1_MID = (FLIP1_START + FLIP1_END) / 2; // ~0.52
      var FLIP_2_MID = (FLIP2_START + FLIP2_END) / 2; // ~0.84

      // Leaves start lying flat over the page beneath (rotateY 0), hinged at the
      // left spine. Shades + casts clear at rest.
      gsap.set([leaf1, leaf2], { rotationY: 0, z: 0, transformOrigin: 'left center' });
      gsap.set([shade1, shade2, cast1, cast2], { opacity: 0 });
      if (mark) gsap.set(mark, { transformOrigin: '50% 50%', rotation: 0 });
      if (leak) gsap.set(leak, { opacity: 0 });
      if (ruleEl) gsap.set(ruleEl, { scaleX: 0.04, transformOrigin: 'left center' });
      if (videoLayer) gsap.set(videoLayer, { opacity: 1 });

      var tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: track,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.6,
          pin: stage,
          pinSpacing: false, // the track already reserves the scroll height
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: function (st) {
            var p = st.progress;

            // Video target: progress within the open window only. Schedule a
            // single coalesced seek; it goes quiet when the target is static.
            targetTime = sub(p, 0, OPEN_END);
            requestSeek();

            // Light-leak swells as the folder opens.
            if (leak) {
              leak.style.opacity = (sub(p, 0, OPEN_END) * 0.55).toFixed(3);
            }

            // Frame counter maps to the image ON SCREEN, not to a flip starting:
            //   001 video (folder opening)
            //   002 shot 1   -> lands when the open video has resolved (OPEN_END)
            //   003 shot 2   -> lands at flip-1 MIDPOINT, as shot 2 is being revealed
            //   004 shot 3   -> lands at flip-2 MIDPOINT, as shot 3 is being revealed
            // Each number arrives WITH its shot, never sits stale during a turn,
            // and 004 is reachable at a stable progress (FLIP_2_MID ~= 0.80).
            if (counterEl) {
              var frame = 1;
              if (p >= FLIP_2_MID) frame = 4;
              else if (p >= FLIP_1_MID) frame = 3;
              else if (p >= OPEN_END) frame = 2;
              var label = ('00' + frame).slice(-3);
              if (counterEl.textContent !== label) counterEl.textContent = label;
            }
          },
        },
      });

      // Every tween below carries an EXPLICIT duration in timeline-progress
      // units (1.0 = the whole scroll). The 8 and the rule both span the full
      // 0 -> 1, which fixes the timeline's total duration at exactly 1.0, so
      // ScrollTrigger maps scroll progress 1:1 onto these positions. (Without
      // explicit durations GSAP's 0.5s default would stretch the total and
      // desync every window.)

      // B: the 8 rotates a clean 90deg into the lying infinity across the FULL
      // scroll. One hit, lots of breathing room (the spine of the piece).
      if (mark) tl.to(mark, { rotation: 90, duration: 1 }, 0);

      // A: the archive rule draws on across the sequence (scaleX, transform
      // only). Hairline grows from the spine.
      if (ruleEl) tl.to(ruleEl, { scaleX: 1, duration: 1 }, 0);

      // Hide the scroll hint quickly once the user starts.
      if (hint) tl.to(hint, { opacity: 0, duration: 0.08 }, 0);

      // Dissolve the video layer out across the tail of the open window so it
      // resolves into shot 1 (its own last frame) with no hard cut. ON the
      // scrubbed timeline so the seam shares the flip playhead and stays in sync.
      // duration is the fraction of the timeline (1.0 = full scroll).
      if (videoLayer) {
        tl.to(
          videoLayer,
          { opacity: 0, duration: 0.06, ease: 'none' },
          OPEN_END - 0.06
        );
      }

      // ---- The page turns ----
      // Each leaf rotates the full 0 -> -180 around the spine with a power2.inOut
      // ease so it reads as a hand deliberately turning a sheet: slow to lift,
      // sweeps through edge-on, settles face-down to the left of the hinge. A
      // small translateZ (z) lift toward the viewer at the midpoint gives the
      // paper thickness/curl rather than a flat plane. The sweeping light
      // (shade) peaks at edge-on and clears at both ends; the cast shadow on the
      // page beneath swells as the leaf passes overhead then clears so the
      // revealed shot lands clean (no residual band at rest).
      function turnLeaf(leaf, shade, cast, start, mid, end) {
        if (!leaf) return;
        var half1 = mid - start;
        var half2 = end - mid;
        var full = end - start;
        // The leaf turns the full 0 -> -180 across its whole window.
        tl.to(leaf, { rotationY: -180, duration: full, ease: 'power2.inOut' }, start);
        // Lift toward the viewer to mid (curl/thickness), settle flat as it lands.
        tl.to(leaf, { z: 90, duration: half1, ease: 'power1.out' }, start);
        tl.to(leaf, { z: 0, duration: half2, ease: 'power1.in' }, mid);
        if (shade) {
          // Light sweeps up to a peak at edge-on (mid) then clears by the end.
          tl.to(shade, { opacity: 0.65, duration: half1, ease: 'power1.in' }, start);
          tl.to(shade, { opacity: 0, duration: half2, ease: 'power1.out' }, mid);
        }
        if (cast) {
          // Cast shadow on the revealed page: swells while the leaf hangs over
          // it (start..mid), clears as the leaf swings off (mid..end).
          tl.to(cast, { opacity: 0.55, duration: half1, ease: 'power1.in' }, start);
          tl.to(cast, { opacity: 0, duration: half2, ease: 'power1.out' }, mid);
        }
      }

      // Flip 1: shot 1 leaf turns to reveal shot 2.
      turnLeaf(leaf1, shade1, cast1, FLIP1_START, FLIP_1_MID, FLIP1_END);

      // Flip 2: shot 2 leaf turns to reveal shot 3 (the resting bottom page).
      turnLeaf(leaf2, shade2, cast2, FLIP2_START, FLIP_2_MID, FLIP2_END);
    }, section);

    instances.push(inst);

    // Switch CSS from the static frame to the live stage.
    section.setAttribute('data-hero-scroll-on', '');
  }

  function buildAll() {
    tearDownAll();
    // Desktop only. matchMedia keeps mobile on the static frame; a resize across
    // the breakpoint rebuilds (matchMedia change listener below).
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;

    eligibleSections().forEach(bootSection);

    if (document.readyState === 'complete') {
      ScrollTrigger.refresh();
    } else {
      window.addEventListener(
        'load',
        function () {
          ScrollTrigger.refresh();
        },
        { once: true }
      );
    }
  }

  // Coalesce rebuilds. A theme-editor edit fires unload THEN load (and a resize
  // can fire repeatedly); without this they would stack up multiple buildAll()
  // calls per edit. We schedule ONE rebuild on the next animation frame, so any
  // burst of events collapses into a single teardown + build (one edit = one
  // rebuild, no flash of an unpinned/blank stage). The cancelled-frame guard
  // also debounces the matchMedia handler.
  var rebuildScheduled = false;
  function scheduleRebuild() {
    if (rebuildScheduled) return;
    rebuildScheduled = true;
    window.requestAnimationFrame(function () {
      rebuildScheduled = false;
      buildAll();
    });
  }

  // Stable handler refs, registered exactly once in init(). Kept module-scoped
  // so nothing leaks if init were ever re-entered.
  var listenersBound = false;
  function onSectionLoad(event) {
    var t = event.target;
    // Only react to our own section; unrelated section edits are ignored.
    if (t && t.matches && t.matches('[data-huit-hero]')) scheduleRebuild();
  }
  function onSectionUnload(event) {
    var t = event.target;
    if (t && t.matches && t.matches('[data-huit-hero]')) scheduleRebuild();
  }
  function onMQ() {
    scheduleRebuild();
  }

  function init() {
    gsap = window.gsap;
    ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) {
      // Globals missing: leave the static frame (we never set scroll-on).
      return;
    }
    gsap.registerPlugin(ScrollTrigger);

    buildAll();

    if (listenersBound) return;
    listenersBound = true;

    // Desktop/mobile boundary: rebuild (coalesced) so we attach or drop
    // scrubbing cleanly without thrashing when dragging across the breakpoint.
    var mq = window.matchMedia(DESKTOP_QUERY);
    if (mq.addEventListener) mq.addEventListener('change', onMQ);
    else if (mq.addListener) mq.addListener(onMQ);

    // ---- Shopify theme-editor lifecycle ----
    // unload + load each schedule the SAME coalesced rebuild, so an edit (which
    // fires both) results in one teardown + build on the next frame. tearDownAll
    // (inside buildAll) reverts the old contexts + cancels video sync first, so
    // nothing leaks. If the merchant hides the section, eligibleSections()
    // empties and the rebuild is a no-op.
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
  }

  function boot() {
    loadScript(GSAP_URL)
      .then(function () {
        return loadScript(ST_URL);
      })
      .then(init)
      .catch(function () {
        // Load failed: the CSS fallback already shows the static frame + overlay,
        // and we never added data-hero-scroll-on, so nothing is hidden.
        tearDownAll();
      });
  }

  if (window.requestIdleCallback) {
    window.requestIdleCallback(boot, { timeout: 1500 });
  } else {
    window.addEventListener('load', boot);
  }
})();
